import {
  getAttNodeProperties,
  getAttValueProperties,
  getNodeProperties,
  getEdgeProperties,
  getRelNodeProperties,
  getRelevantEntityIdFromNode,
  propertyCount,
  removeVisPrefix,
  renderGraphInfo
} from "./helpers"
import { getUid } from 'Services/WeaverUtils'
import { store } from '~/Store'

let COUNT = {}
const NODE_LIMIT = () => store.getState().graph.nodeLimit

const bookNode = (id) => `node-${id}`
const bookAttributeEdge = (id) => `attribute-edge-${id}`
const bookAttributeEdgeFrom = (id) => `attribute-edge-from-${id}`
const bookAttributeEdgeTo = (id) => `attribute-edge-to-${id}`
const bookAttributeValue = (id) => `attribute-value-${id}`
const bookAttributeNode = (id) => `attribute-node-${id}`
const bookRelationEdge = (id) => `relation-edge-${id}`
const bookRelationEdgeFrom = (id) => `relation-edge-from-${id}`
const bookRelationEdgeTo = (id) => `relation-edge-to-${id}`
const bookRelationNode = (id) => `relation-node-${id}`

// CREATES agenda AND visWvrMap
export const showHide = (canonicalNodeIds, richNodesMap, opts) => {
  const visitedNodes = []
  const agenda = _.reduce(canonicalNodeIds, (acc, id) => {
    const n = richNodesMap[id]
    if (!visitedNodes.some(visited => visited === n) && n !== undefined) {
      visitedNodes.push(n)
      return _.concat(
        acc,
        book(n, opts, richNodesMap),
        caseFunction(n, opts, visitedNodes, richNodesMap)
      )
    }
    return acc
  }, [])

  if (_.keys(COUNT).length >= NODE_LIMIT()) {
    renderGraphInfo({
      description: 'It is recommended that you refine your query to be more specific.',
      message: `${NODE_LIMIT()} nodes have been rendered.`,
    })
  }

  COUNT = {};

  return {
    visIds: agenda,
    visWvrMap: buildVisIdToWvrMap(agenda, richNodesMap, visitedNodes)
  }
}

const handleAttBooking = (rec, { showAttributes, showRelevantMetaNodes, showAllMetaNodes }, richNodesMap) => {
  if (_.keys(COUNT).length >= NODE_LIMIT()) {
    return []
  }
  const { attNode } = rec
  const id = getUid(attNode)
  if (!COUNT[id]) {
    COUNT[id] = attNode
  }
  const richAttNode = richNodesMap[getUid(id)] || attNode
  if (showAttributes && ((showRelevantMetaNodes && propertyCount(richAttNode) > 0) || showAllMetaNodes)) {
    const flattened = _.flatten([
      bookAttributeEdgeFrom(id),
      bookAttributeEdgeTo(id),
      bookAttributeValue(id),
      bookAttributeNode(id)
    ])
    return flattened
  } else if (showAttributes) {
    const flattened = _.flatten([
      bookAttributeEdge(id),
      bookAttributeValue(id)
    ])
    return flattened
  }
  return []
}

const handleRelBooking = (rec, { showRelevantMetaNodes, showAllMetaNodes, showAttributes }, richNodesMap) => {
  if (_.keys(COUNT).length >= NODE_LIMIT()) {
    return []
  }
  const { relNode, toNode, fromNode } = rec
  const id = getUid(relNode)
  const richRelNode = richNodesMap[getUid(id)] || relNode
  if (
    !showAttributes &&
    (toNode instanceof Weaver.AttributeNode || fromNode instanceof Weaver.AttributeNode)
  ) {
    return []
  }
  if ((showRelevantMetaNodes && propertyCount(richRelNode) > 0) || showAllMetaNodes) {
    if (!COUNT[id]) {
      COUNT[id] = relNode
    }
    const flattened = _.flatten([
      bookRelationEdgeFrom(id),
      bookRelationEdgeTo(id),
      bookRelationNode(id)
    ])
    return flattened
  } else {
    return [ bookRelationEdge(id) ] // default case
  }
}

const handleNodeBooking = (item) => {
  if (_.keys(COUNT).length >= NODE_LIMIT()) {
    return []
  }
  const id = getUid(item)
  if (!COUNT[id]) {
    COUNT[id] = item
  }
  return [ bookNode(id) ]
}

// UPDATES AGENDA WITH NODE THAT HAS BEEN BOOKED
const book = (item, opts, richNodesMap) => {
  if (item instanceof Weaver.AttributeNode) {
    return []
  } else if (item instanceof Weaver.RelationNode) {
    return []
  } else if (item.attNode) {
    return handleAttBooking(item, opts, richNodesMap)
  } else if (item.relNode) {
    return handleRelBooking(item, opts, richNodesMap)
  } else if (item instanceof Weaver.Node) {
    return handleNodeBooking(item)
  } else {
    throw new Error(`Congrats, you created a new Weaver class unbeknownst to us...`, { cause: item })
  }
}

const caseFunction = (n, opts, visitedNodes, richNodesMap) => {
  const { showAttributes } = opts
  const relRecordsOut = _.flatMap(n._relations, (rel) => rel._records)
  const relRecordsIn = _.flatMap(n._relationsIn, (rel) => rel._records)
  const relRecords = [...relRecordsOut, ...relRecordsIn]
  let attRecords = []
  if (showAttributes) {
    attRecords = _.flatMap(n._attributes, (att) => att._records)
  }
  return _.concat(
    traverseRecords(relRecords, opts, visitedNodes, richNodesMap),
    traverseRecords(attRecords, opts, visitedNodes, richNodesMap)
  )
}

export const traverseRecords = (records, opts, visitedNodes, richNodesMap) => {
  const { showRelevantMetaNodes, showAllMetaNodes } = opts
  return _.reduce(records, (acc, rec) => {
    if (_.keys(COUNT).length >= NODE_LIMIT()) {
      return acc
    }

    let { toNode, relNode, attNode, fromNode } = rec

    if (toNode) {
      toNode = richNodesMap[getUid(toNode)] || toNode
    }
    if (fromNode) {
      fromNode = richNodesMap[getUid(fromNode)] || fromNode
    }
    if (attNode) {
      attNode = richNodesMap[getUid(attNode)] || attNode
    }
    if (relNode) {
      relNode = richNodesMap[getUid(relNode)] || relNode
    }

    acc = _.concat(acc, book(rec, opts, richNodesMap))

    if (fromNode && !visitedNodes.some(visited => visited === fromNode)) {
      visitedNodes.push(fromNode)
      acc = _.concat(
        acc,
        book(fromNode, opts, richNodesMap),
        caseFunction(fromNode, opts, visitedNodes, richNodesMap)
      )
    }

    if (toNode && !visitedNodes.some(visited => visited === toNode)) {
      visitedNodes.push(toNode)
      acc = _.concat(acc,
        book(toNode, opts, richNodesMap),
        caseFunction(toNode, opts, visitedNodes, richNodesMap)
      )
    }

    if (relNode && !visitedNodes.some(visited => visited === relNode)) {
      visitedNodes.push(relNode)
      acc = _.concat(acc, book(rec, opts, richNodesMap))
      if (showRelevantMetaNodes || showAllMetaNodes) {
        acc = _.concat(acc,
          caseFunction(relNode, opts, visitedNodes, richNodesMap)
        )
      }
    }

    if (attNode && !visitedNodes.some(visited => visited === attNode)) {
      visitedNodes.push(attNode)
      acc = _.concat(acc, book(rec, opts, richNodesMap))
      if (showRelevantMetaNodes || showAllMetaNodes) {
        acc = _.concat(acc,
          caseFunction(attNode, opts, visitedNodes, richNodesMap)
        )
      }
    }

    return acc
  }, [])
}

// ITERATES OVER agenda AND queryResults
// BUILDS visWvrMap
// { [visId]: Weaver.Node, ... }
const buildVisIdToWvrMap = (agenda, richNodesMap, visitedNodes) => {
  const visWvrMap = {}

  _.map(agenda, (visId) => {
    const id = removeVisPrefix(visId)
    visWvrMap[visId] = richNodesMap[id] || visitedNodes.find((node) => getUid(node) === id)
  })

  return visWvrMap
}

// BUILDS graphData STRUCT FROM visWvrMap
// USES visWvrMap KEYS TO CREATE graphData.nodes AND graphData.edges
export const buildGraphData = (visWvrMap) => {
  const middleEdgeProps = {
    arrowStrikethrough: true,
    arrows: {
      middle: {
        enabled: true,
        scaleFactor: 0.5,
      },
      to: false
    }
  }

  const data = _.reduce(visWvrMap, (graphData, node, key) => {
    const { fromNode, toNode } = node
    if (_.startsWith(key, 'node')) {
      graphData.nodes = {
        ...graphData.nodes,
        [key]: getNodeProperties(node, key)
      }
    } else if (_.startsWith(key, 'relation-node')) {
      graphData.nodes = {
        ...graphData.nodes,
        [key]: getRelNodeProperties(node, node.key, false, key)
      }
    } else if (_.startsWith(key, 'relation-edge-from')) {
      graphData.edges = {
        ...graphData.edges,
        [key]: getEdgeProperties(node.key, getRelevantEntityIdFromNode(fromNode), getRelevantEntityIdFromNode(node), node, true, key, middleEdgeProps)
      }
    } else if (_.startsWith(key, 'relation-edge-to')) {
      graphData.edges = {
        ...graphData.edges,
        [key]: getEdgeProperties(node.key, getRelevantEntityIdFromNode(node), getRelevantEntityIdFromNode(toNode), node, true, key, middleEdgeProps)
      }
    } else if (_.startsWith(key, 'relation-edge')) {
      graphData.edges = {
        ...graphData.edges,
        [key]: getEdgeProperties(node.key, getRelevantEntityIdFromNode(fromNode), getRelevantEntityIdFromNode(toNode), node, false, key)
      }
    } else if (_.startsWith(key, 'attribute-node')) {
      graphData.nodes = {
        ...graphData.nodes,
        [key]: getAttNodeProperties(node, key)
      }
    } else if (_.startsWith(key, 'attribute-edge-from')) {
      graphData.edges = {
        ...graphData.edges,
        [key]: getEdgeProperties(node.key, getRelevantEntityIdFromNode(fromNode), getRelevantEntityIdFromNode(node), node, true, key, middleEdgeProps)
      }
    } else if (_.startsWith(key, 'attribute-edge-to')) {
      graphData.edges = {
        ...graphData.edges,
        [key]: getEdgeProperties(node.key, getRelevantEntityIdFromNode(node), 'attribute-value-' + getUid(node), node, true, key, middleEdgeProps)
      }
    } else if (_.startsWith(key, 'attribute-edge')) {
      graphData.edges = {
        ...graphData.edges,
        [key]: getEdgeProperties(node.key, getRelevantEntityIdFromNode(fromNode), 'attribute-value-' + getUid(node), node, false, key)
      }
    } else if (_.startsWith(key, 'attribute-value')) {
      graphData.nodes = {
        ...graphData.nodes,
        [key]: getAttValueProperties(node, key)
      }
    }
    return graphData
  }, {})

  return data
}
